import React from 'react';
import { Link } from 'react-router-dom';
import { Users, UserCheck, Handshake, IndianRupee } from 'lucide-react';
import { Button } from '../ui/button';
import { supportFeatures, personalityTraits } from '../../data/mock';

const iconMap = {
  Users: Users,
  UserCheck: UserCheck,
  Handshake: Handshake,
  IndianRupee: IndianRupee
};

const SupportSection = () => {
  return (
    <section data-testid="support-section" className="relative py-20 md:py-32 bg-cream-dark overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-richolive/10 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-pragmatic/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
            How We Help
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-deepwalnut mb-6">
            We Work Closely With <span className="text-richolive">Our Participants</span>
          </h2>
          <p className="text-heavyrain text-lg max-w-2xl mx-auto">
            Comprehensive support system to help students excel at the global stage
          </p>
        </div>

        {/* Support Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {supportFeatures.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <div
                key={index}
                className="bg-cream border border-pragmatic/30 rounded-2xl p-6 hover:border-dwarfpony hover:shadow-xl transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pragmatic/30 to-richolive/20 flex items-center justify-center mb-4 group-hover:from-dwarfpony/30 group-hover:to-richolive/30 transition-colors">
                  <Icon className="w-6 h-6 text-dwarfpony" />
                </div>
                <h3 className="text-deepwalnut font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-heavyrain text-sm leading-relaxed">{feature.description}</p>
              </div>
            );
          <div className="text-center mt-12 mb-16">
            <Link to="/how-we-help">
              <Button 
                variant="outline"
                className="border-dwarfpony text-dwarfpony hover:bg-dwarfpony hover:text-white transition-colors"
              >
                Learn More About Our Support System
              </Button>
            </Link>
          </div>
          })}
        </div>

        {/* Personality Traits */}
        <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 md:p-10 shadow-lg">
          <h3 className="text-deepwalnut font-bold text-xl mb-2 text-center">Personality Development Impact</h3>
          <p className="text-heavyrain text-sm text-center mb-8">Based on FIRST Global participant outcomes</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {personalityTraits.map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative w-20 h-20 mx-auto mb-3">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      className="text-pragmatic/30"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="6"
                      fill="none"
                      strokeDasharray={`${(item.percentage / 100) * 226} 226`}
                      className="text-richolive transition-all duration-1000"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-deepwalnut font-bold text-lg">
                    {item.percentage}%
                  </span>
                </div>
                <p className="text-deepwalnut text-sm font-medium">{item.trait}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportSection;
